'use client';

import React, { useMemo, useState } from 'react';

import { Color } from '@bytel/product-wall';
import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Button,
    Column,
    Columns,
    DirectionEnum,
    FlexBox,
    GapSize,
    IconName,
    Justify,
    Spacer,
    SpacerSize,
    Text,
    TextMarkup,
    View,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ProductChildFull, ProductParentFull } from '@app-types/product';

import { EnergyLabel } from '@components/energy-label';
import { ImageThumbor } from '@components/image-thumbor';

import { Das } from '@product-detail/components';
import { sendProductPreviewTracking } from '@product-detail/helpers';

export type ProductPreviewProps = {
    type: WallType;
    features: ProductParentFull['features'];
    currentProduct: ProductChildFull;
    setCurrentProduct: (color: string, capacity?: string) => void;
    isRenewal: boolean;
    openEnergyLabelModal?: () => void;
};

export function ProductPreview({
    type,
    features,
    currentProduct,
    setCurrentProduct,
    isRenewal,
    openEnergyLabelModal,
}: Readonly<ProductPreviewProps>) {
    const [previewIndex, setPreviewIndex] = useState<number>(0);
    const [thumbnailIndex, setThumbnailIndex] = useState<number>(0);

    const isPhone = useMemo(() => type === WallType.PHONE || type === WallType.SALES_PHONE, [type]);

    const handleChangePreview = (index: number) => {
        setPreviewIndex(index);
        sendProductPreviewTracking(isRenewal);
    };

    const currentPreview = useMemo(
        () => currentProduct.medias[previewIndex] ?? (currentProduct.medias[0] as string),
        [currentProduct.gencode, previewIndex],
    );

    return (
        <div className='is-sticky-top-desktop product-preview'>
            <Columns align={Align.CENTER} mobile marginless>
                <div className='is-relative'>
                    <div className='product-thumbnail'>
                        <Column narrow verticalAlign={Align.CENTER}>
                            <FlexBox align={Align.CENTER} direction={DirectionEnum.COLUMN} gap={GapSize.TWO}>
                                <Button
                                    iconName={IconName.ARROW_UP}
                                    className={classNames('button-clear', thumbnailIndex > 0 ? '' : 'is-invisible')}
                                    onClick={() => setThumbnailIndex(thumbnailIndex - 1)}
                                >
                                    <Text markup={TextMarkup.SPAN} className='sr-only'>
                                        Voir les images précédentes
                                    </Text>
                                </Button>
                                {currentProduct?.medias.map(
                                    (media, index) =>
                                        index >= thumbnailIndex &&
                                        index <= thumbnailIndex + 2 && (
                                            <Button
                                                key={`${media}-${index}`}
                                                className='button-clear h-auto'
                                                onClick={() => handleChangePreview(index)}
                                            >
                                                <ImageThumbor
                                                    src={`/${media}`}
                                                    alt=''
                                                    width={40}
                                                    height={isPhone ? 80 : 35}
                                                    position={0}
                                                    fetchPriority='high'
                                                />
                                            </Button>
                                        ),
                                )}
                                <Button
                                    iconName={IconName.ARROW_DOWN}
                                    className={classNames(
                                        'button-clear',
                                        thumbnailIndex < currentProduct?.medias.length - 3 ? '' : 'is-invisible',
                                    )}
                                    onClick={() => setThumbnailIndex(thumbnailIndex + 1)}
                                >
                                    <Text markup={TextMarkup.SPAN} className='sr-only'>
                                        Voir les images suivantes
                                    </Text>
                                </Button>
                            </FlexBox>
                        </Column>
                    </div>
                    <Column>
                        <ImageThumbor
                            src={`/${currentPreview}`}
                            alt={`Photo du produit ${currentProduct.parentTitle} - vue ${previewIndex + 1}`}
                            aria-hidden='true'
                            width={200}
                            height={isPhone ? 400 : 200}
                            id='productPreview'
                            position={0}
                            fetchPriority='high'
                        />
                    </Column>
                </div>
            </Columns>

            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justify.CENTER} className='gap-medium'>
                {features.color.map(({ name, value }) => (
                    <Color
                        key={name}
                        color={value}
                        isFocus={currentProduct.color === name}
                        onClick={() => setCurrentProduct(name, currentProduct.capacity)}
                    />
                ))}
            </View>
            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justify.CENTER} className='gap-medium'>
                {currentProduct.characteristics.energyLabel && (
                    <EnergyLabel
                        rating={currentProduct.characteristics.energyLabel.label}
                        onClick={openEnergyLabelModal}
                    />
                )}
                <Das das={currentProduct.das} />
            </View>
        </div>
    );
}
